import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Drawer,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useCarwashSearch } from '../../hooks/useCarwashSearch';
import { Carwash, MapBounds, MapFilters } from '../../types/CarwashType';
import PartnerMap, { PartnerMapHandle } from '../../components/MapaParceiros/PartnerMap';
import PartnerList from '../../components/MapaParceiros/PartnerList';
import { AppHeader } from '../../components/shared/AppHeader';

const DEFAULT_FILTERS: MapFilters = {
  search: '',
  plan: '',
  service: '',
  categoryId: '',
  rating: '',
  city: '',
  state: '',
  order: 'distance',
};

const MOBILE_LIST_DRAWER_HEIGHT = '55vh';

function MapaParceiros() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { coords, denied, defaultCoords } = useGeolocation();
  const { carwashes, loading, error, search } = useCarwashSearch();

  const [pendingFilters, setPendingFilters] = useState<MapFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<MapFilters>(DEFAULT_FILTERS);
  const [currentBounds, setCurrentBounds] = useState<MapBounds | null>(null);
  const [selectedCarwashId, setSelectedCarwashId] = useState<number | null>(null);
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  const [locationDeniedSnackbar, setLocationDeniedSnackbar] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [cityNotFoundSnackbar, setCityNotFoundSnackbar] = useState(false);

  const partnerMapRef = useRef<PartnerMapHandle>(null);

  useEffect(() => {
    if (denied) setLocationDeniedSnackbar(true);
  }, [denied]);

  const center = useMemo(() => coords ?? defaultCoords, [coords, defaultCoords]);

  const appliedFiltersRef = useRef<MapFilters>(appliedFilters);
  useEffect(() => { appliedFiltersRef.current = appliedFilters; }, [appliedFilters]);

  const handleBoundsChange = useCallback(
    (bounds: MapBounds) => {
      setCurrentBounds(bounds);
      search(bounds, appliedFiltersRef.current);
    },
    [search]
  );

  function handlePendingChange(field: keyof MapFilters, value: string) {
    setPendingFilters((prev) => ({ ...prev, [field]: value }));
  }

  const handleSearch = useCallback(async () => {
    const filters = pendingFilters;
    setAppliedFilters(filters);
    appliedFiltersRef.current = filters;

    if (filters.city && partnerMapRef.current) {
      setGeocoding(true);
      const found = await partnerMapRef.current.panToCity(filters.city, filters.state);
      setGeocoding(false);

      if (!found) {
        setCityNotFoundSnackbar(true);
        // Fallback: busca com os bounds atuais mesmo sem encontrar a cidade
        if (currentBounds) search(currentBounds, filters);
      }
      // Se encontrou: fitBounds dispara onIdle → handleBoundsChange → search com novos bounds
    } else {
      if (currentBounds) search(currentBounds, filters);
    }
  }, [pendingFilters, currentBounds, search]);

  const handleClear = useCallback(() => {
    setPendingFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    appliedFiltersRef.current = DEFAULT_FILTERS;
    if (currentBounds) search(currentBounds, DEFAULT_FILTERS);
  }, [currentBounds, search]);

  const handleCarwashSelect = useCallback((carwash: Carwash | null) => {
    setSelectedCarwashId(carwash?.id ?? null);
    if (carwash && isMobile) setListDrawerOpen(false);
  }, [isMobile]);

  const handleListCarwashSelect = useCallback((carwash: Carwash) => {
    setSelectedCarwashId(carwash.id);
    if (isMobile) setListDrawerOpen(false);
  }, [isMobile]);

  const hasActiveFilters = Boolean(
    pendingFilters.search || pendingFilters.city || pendingFilters.state || pendingFilters.rating
  );

  const mapElement = (
    <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
      <PartnerMap
        ref={partnerMapRef}
        carwashes={carwashes}
        userCoords={coords}
        center={center}
        selectedCarwashId={selectedCarwashId}
        onBoundsChange={handleBoundsChange}
        onCarwashSelect={handleCarwashSelect}
      />
    </Box>
  );

  const listElement = (
    <PartnerList
      carwashes={carwashes}
      loading={loading}
      error={error}
      selectedCarwashId={selectedCarwashId}
      onCarwashSelect={handleListCarwashSelect}
    />
  );

  return (
    <>
      <AppHeader variant="dark" />

      <Box
        sx={{
          height: '100vh',
          pt: '72px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* Page header */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.25, md: 1.5 },
            bgcolor: '#fff',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography fontWeight={800} color="#2F3857" sx={{ lineHeight: 1.2, fontSize: { xs: 20, md: 26 } }}>
            Encontre um parceiro perto de você
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            +500 lava-rápidos credenciados em SP, RJ, BH e Brasília
          </Typography>
        </Box>

        {/* Inline filter bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, md: 3 },
            py: 1.5,
            bgcolor: '#f8fcff',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <TextField
            size="small"
            label="Buscar por nome ou endereço"
            value={pendingFilters.search}
            onChange={(e) => handlePendingChange('search', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            autoComplete="off"
            sx={{ flex: '3 1 200px' }}
          />
          <TextField
            size="small"
            label="Cidade"
            value={pendingFilters.city}
            onChange={(e) => handlePendingChange('city', e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            autoComplete="off"
            sx={{ flex: '1 1 120px' }}
          />
          <TextField
            size="small"
            label="UF"
            value={pendingFilters.state}
            onChange={(e) => handlePendingChange('state', e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            inputProps={{ maxLength: 2 }}
            autoComplete="off"
            sx={{ flex: '0 0 72px' }}
          />
          <FormControl size="small" sx={{ flex: '1 1 130px' }}>
            <InputLabel>Nota</InputLabel>
            <Select
              value={pendingFilters.rating}
              label="Nota"
              onChange={(e) => handlePendingChange('rating', e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="3">3+ estrelas</MenuItem>
              <MenuItem value="4">4+ estrelas</MenuItem>
              <MenuItem value="5">5 estrelas</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: '1 1 150px' }}>
            <InputLabel>Ordenar</InputLabel>
            <Select
              value={pendingFilters.order}
              label="Ordenar"
              onChange={(e) => handlePendingChange('order', e.target.value as MapFilters['order'])}
            >
              <MenuItem value="distance">Mais próximo</MenuItem>
              <MenuItem value="rating">Melhor avaliação</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={geocoding ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={geocoding}
            sx={{
              bgcolor: '#00b4e4',
              '&:hover': { bgcolor: '#0099c6' },
              '&.Mui-disabled': { bgcolor: '#00b4e4', opacity: 0.75 },
              textTransform: 'none',
              flexShrink: 0,
              px: 3,
              height: 40,
            }}
          >
            {geocoding ? 'Buscando...' : 'Buscar'}
          </Button>
          {hasActiveFilters && (
            <Button
              variant="text"
              onClick={handleClear}
              sx={{ textTransform: 'none', color: 'text.secondary', flexShrink: 0, px: 1 }}
            >
              Limpar
            </Button>
          )}
        </Box>

        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Desktop: list | map */}
          {!isMobile && (
            <>
              <Box sx={{ width: 360, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fcff', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {carwashes.length} parceiro{carwashes.length !== 1 ? 's' : ''} na área atual
                  </Typography>
                </Box>
                {listElement}
              </Box>

              {mapElement}
            </>
          )}

          {/* Mobile: map fullscreen + list drawer */}
          {isMobile && (
            <>
              {mapElement}

              <Drawer
                anchor="bottom"
                open={listDrawerOpen}
                onClose={() => setListDrawerOpen(false)}
                PaperProps={{
                  sx: {
                    height: MOBILE_LIST_DRAWER_HEIGHT,
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                    Parceiros encontrados ({carwashes.length})
                  </Typography>
                  <IconButton size="small" onClick={() => setListDrawerOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                {listElement}
              </Drawer>

              {!listDrawerOpen && !selectedCarwashId && (
                <Button
                  variant="contained"
                  startIcon={<ListIcon />}
                  onClick={() => setListDrawerOpen(true)}
                  sx={{
                    position: 'absolute',
                    bottom: 24,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: '#00b4e4',
                    '&:hover': { bgcolor: '#0099c6' },
                    textTransform: 'none',
                    borderRadius: 6,
                    px: 3,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Ver lista ({carwashes.length})
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={locationDeniedSnackbar}
        autoHideDuration={5000}
        onClose={() => setLocationDeniedSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" onClose={() => setLocationDeniedSnackbar(false)}>
          Localização não disponível. Exibindo mapa centralizado em São Paulo.
        </Alert>
      </Snackbar>

      <Snackbar
        open={cityNotFoundSnackbar}
        autoHideDuration={4000}
        onClose={() => setCityNotFoundSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setCityNotFoundSnackbar(false)}>
          Cidade não encontrada. Verifique o nome e tente novamente.
        </Alert>
      </Snackbar>
    </>
  );
}

export default MapaParceiros;
